import Vue from 'vue'
import * as divergenceService from './divergenceService'
import {
  DIVERGENCE_KINDS,
  DIVERGENCE_STATUSES,
  DIVERGENCE_TYPES,
  buildOptions,
  buildPaginationParams,
} from './divergenceHelpers'

const state = {
  divergences: [],
  divergenceDelta: null,
  divergenceFileUrls: {},
  divergenceCheckIn: null,
  isDivergenceCheckInLoading: false,
  divergenceCheckInFailed: false,
  isDivergenceFilesLoading: false,
  isDivergenceSimulating: false,
  isDivergenceReviewing: false,
}

const getters = {
  divergences: (state) => state.divergences,
  divergenceDelta: (state) => state.divergenceDelta,
  divergenceFileUrls: (state) => state.divergenceFileUrls,
  divergenceCheckIn: (state) => state.divergenceCheckIn,
  isDivergenceCheckInLoading: (state) => state.isDivergenceCheckInLoading,
  divergenceCheckInFailed: (state) => state.divergenceCheckInFailed,
  isDivergenceFilesLoading: (state) => state.isDivergenceFilesLoading,
  isDivergenceSimulating: (state) => state.isDivergenceSimulating,
  isDivergenceReviewing: (state) => state.isDivergenceReviewing,

  divergenceStatusOptions: (_state, _getters, _rootState, rootGetters) => {
    const i18n = rootGetters['i18n'] || Vue.prototype.$t
    return buildOptions(DIVERGENCE_STATUSES, i18n, 'divergence.status')
  },

  divergenceTypeOptions: (_state, _getters, _rootState, rootGetters) => {
    const i18n = rootGetters['i18n'] || Vue.prototype.$t
    return buildOptions(DIVERGENCE_TYPES, i18n, 'divergence.type')
  },

  divergenceKindOptions: (_state, _getters, _rootState, rootGetters) => {
    const i18n = rootGetters['i18n'] || Vue.prototype.$t
    return buildOptions(DIVERGENCE_KINDS, i18n, 'divergence.kind')
  },
}

const actions = {
  async fetchDivergences({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)
    const params = buildPaginationParams({
      itemsPerPage: rootState.paginate.itemsPerPage,
      currentPage: rootState.paginate.currentPage,
      filters: rootState.filters.filters,
    })

    try {
      const { data } = await divergenceService.list(params)
      commit('setDivergences', data.result)
      commit('setTotalPages', data.total)
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  /**
   * Delta exato do lote. A soma mostrada na listagem é apenas indicativa: as taxas de mobília e
   * enxoval incidem sobre a base já corrigida pela metragem, então não somam linearmente.
   */
  /**
   * `approvedDisplacementValue` is the amount the reviewer typed for a displacement. Without it the
   * backend previews with the host's suggestion, which is what the field starts with.
   */
  async simulateDivergences({ commit, dispatch }, { divergenceIds, approvedDisplacementValue }) {
    commit('setIsDivergenceSimulating', true)
    commit('setDivergenceDelta', null)
    try {
      const { data } = await divergenceService.simulate({
        divergence_ids: divergenceIds,
        approved_displacement_value: approvedDisplacementValue,
      })
      commit('setDivergenceDelta', data)
      return data
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
      return null
    } finally {
      commit('setIsDivergenceSimulating', false)
    }
  },

  /**
   * Checkin do host. O erro não vira toast nem some como "sem localização": o hcheck pode estar
   * fora do ar, e a tela tem de dizer que não conseguiu consultar, o que é diferente de o
   * agendamento não ter localização registrada.
   */
  async fetchDivergenceCheckIn({ commit }, appointmentId) {
    commit('setDivergenceCheckIn', null)
    commit('setDivergenceCheckInFailed', false)
    commit('setIsDivergenceCheckInLoading', true)
    try {
      const { data } = await divergenceService.checkIn(appointmentId)
      commit('setDivergenceCheckIn', data)
    } catch (error) {
      commit('setDivergenceCheckInFailed', true)
    } finally {
      commit('setIsDivergenceCheckInLoading', false)
    }
  },

  /**
   * Uma URL assinada por foto. Falha de uma não derruba as outras: a miniatura fica vazia e o
   * analista ainda consegue decidir pelo resto.
   */
  async fetchDivergenceFileUrls({ commit }, pairs) {
    commit('setDivergenceFileUrls', {})
    if (!pairs || !pairs.length) return

    commit('setIsDivergenceFilesLoading', true)
    const urls = {}
    await Promise.all(
      pairs.map(({ divergenceId, fileId }) =>
        divergenceService
          .fileDownloadURL(divergenceId, fileId)
          .then(({ data }) => {
            urls[fileId] = data.url
          })
          .catch(() => {})
      )
    )
    commit('setDivergenceFileUrls', urls)
    commit('setIsDivergenceFilesLoading', false)
  },

  /**
   * Approves the reimbursement and pays the host who travelled. The amount is what the review
   * decided for the trip; with chargeRealEstate it is also debited from the real estate and the host
   * gets hostPercent of it. A null hostPercent leaves the backend on the contract's rate.
   */
  async approveReimbursements(
    { commit, dispatch },
    { divergenceIds, reviewComment, approvedValue, chargeRealEstate, hostPercent }
  ) {
    commit('setIsDivergenceReviewing', true)
    try {
      await divergenceService.approveReimbursement({
        divergence_ids: divergenceIds,
        review_comment: reviewComment,
        approved_reimbursement_value: approvedValue,
        charge_real_estate: Boolean(chargeRealEstate),
        reimbursement_host_percent: hostPercent ?? null,
      })
      await dispatch('fetchDivergences')
      return true
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
      return false
    } finally {
      commit('setIsDivergenceReviewing', false)
    }
  },

  async approveDivergences(
    { commit, dispatch },
    { divergenceIds, reviewComment, approvedDisplacementValue },
  ) {
    commit('setIsDivergenceReviewing', true)
    try {
      await divergenceService.approve({
        divergence_ids: divergenceIds,
        review_comment: reviewComment,
        approved_displacement_value: approvedDisplacementValue,
      })
      await dispatch('fetchDivergences')
      return true
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
      return false
    } finally {
      commit('setIsDivergenceReviewing', false)
    }
  },

  /** Registra a contestação. Não move dinheiro. */
  async contestDivergences({ commit, dispatch }, { divergenceIds, contestReason }) {
    commit('setIsDivergenceReviewing', true)
    try {
      await divergenceService.contest({ divergence_ids: divergenceIds, contest_reason: contestReason })
      await dispatch('fetchDivergences')
      return true
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
      return false
    } finally {
      commit('setIsDivergenceReviewing', false)
    }
  },

  /** Estorna o ajuste. O motivo já está gravado, não precisa reenviar. */
  async reverseDivergences({ commit, dispatch }, { divergenceIds }) {
    commit('setIsDivergenceReviewing', true)
    try {
      await divergenceService.reverse({ divergence_ids: divergenceIds })
      await dispatch('fetchDivergences')
      return true
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
      return false
    } finally {
      commit('setIsDivergenceReviewing', false)
    }
  },

  async refuseDivergences({ commit, dispatch }, { divergenceIds, reviewComment }) {
    commit('setIsDivergenceReviewing', true)
    try {
      await divergenceService.refuse({
        divergence_ids: divergenceIds,
        review_comment: reviewComment,
      })
      await dispatch('fetchDivergences')
      return true
    } catch ({ data }) {
      dispatch('showErrorMessage', data?.message)
      return false
    } finally {
      commit('setIsDivergenceReviewing', false)
    }
  },
}

const mutations = {
  setDivergences: (state, divergences) => {
    state.divergences = divergences
  },

  setDivergenceDelta: (state, divergenceDelta) => {
    state.divergenceDelta = divergenceDelta
  },

  setDivergenceFileUrls: (state, divergenceFileUrls) => {
    state.divergenceFileUrls = divergenceFileUrls
  },

  setDivergenceCheckIn: (state, divergenceCheckIn) => {
    state.divergenceCheckIn = divergenceCheckIn
  },

  setIsDivergenceCheckInLoading: (state, status) => {
    state.isDivergenceCheckInLoading = status
  },

  setDivergenceCheckInFailed: (state, status) => {
    state.divergenceCheckInFailed = status
  },

  setIsDivergenceFilesLoading: (state, status) => {
    state.isDivergenceFilesLoading = status
  },

  setIsDivergenceSimulating: (state, status) => {
    state.isDivergenceSimulating = status
  },

  setIsDivergenceReviewing: (state, status) => {
    state.isDivergenceReviewing = status
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
